import type { RemediationPlan } from '../types/project';

interface RemediationPlanPanelProps {
  remediation: RemediationPlan;
}

/**
 * The path back to a green deadline. Rendered on the brand navy (matching the
 * app's sidebar) so it reads as the action zone against the near-white page:
 * the ordered recovery moves, each stating the schedule it buys back, under one
 * primary "apply the plan" call to action.
 */
export function RemediationPlanPanel({
  remediation,
}: RemediationPlanPanelProps) {
  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-3xl bg-gradient-to-br from-[#0E1B3A] via-[#152A52] to-[#1C3870] p-8 text-white shadow-[0_40px_90px_-40px_rgba(14,27,58,0.8)]">
      {/* Ambient brand glow, top-end corner. */}
      <div className="pointer-events-none absolute -end-24 -top-24 h-72 w-72 rounded-full bg-[#22D1C5] opacity-[0.12] blur-[120px]" />

      <div className="relative grid flex-1 gap-8 lg:grid-cols-12">
        {/* Framing + primary action. */}
        <div className="flex flex-col lg:col-span-4">
          <span className="inline-flex w-max items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#22D1C5]">
            <svg
              viewBox="0 0 24 24"
              className="h-3.5 w-3.5"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
            </svg>
            Path to green
          </span>
          <h2 className="mt-3 text-2xl font-semibold leading-tight tracking-tight">
            {remediation.headline}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-white/60">
            {remediation.blurb}
          </p>

          <div className="mt-6 inline-flex w-max items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-xs font-medium text-white/70 ring-1 ring-inset ring-white/10">
            Recovers
            <span className="font-semibold text-[#22D1C5]">
              {remediation.recoversLabel}
            </span>
          </div>

          <button
            type="button"
            className="mt-6 inline-flex w-max items-center gap-2 rounded-xl bg-gradient-to-r from-[#1678C2] to-[#22D1C5] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_12px_30px_-10px_rgba(34,209,197,0.6)] transition-transform hover:scale-[1.02] active:scale-95"
          >
            Apply recovery plan
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4 rtl:rotate-180"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        {/* The ordered moves. */}
        <ol className="flex flex-col gap-3 lg:col-span-8">
          {remediation.steps.map((step, stepIndex) => (
            <li
              key={step.id}
              className="group flex items-start gap-4 rounded-2xl bg-white/[0.04] p-4 ring-1 ring-inset ring-white/10 transition-colors hover:bg-white/[0.07]"
            >
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white/80 ring-1 ring-inset ring-white/10">
                {stepIndex + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <h4 className="text-sm font-medium text-white transition-colors group-hover:text-[#22D1C5]">
                    {step.title}
                  </h4>
                  <span className="rounded-full bg-[#22D1C5]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#22D1C5]">
                    {step.impactLabel}
                  </span>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-white/55">
                  {step.detail}
                </p>
              </div>
              <button
                type="button"
                className="hidden shrink-0 rounded-full border border-white/15 px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:bg-white hover:text-[#0E1B3A] sm:block"
              >
                {step.actionLabel}
              </button>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
